import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Check, DollarSign, Users, ReceiptText, Banknote } from "lucide-react";
import { drName, formatDateShort } from "@/lib/utils";
import { useApp } from "@/context/AppContext";

// Ecuadorian banks — kept as a config constant so admin can extend later.
const EC_BANKS = [
  "Banco Pichincha", "Banco Guayaquil", "Banco del Pacífico", "Produbanco",
  "Banco Bolivariano", "Banco Internacional", "Banco del Austro", "BanEcuador",
  "Cooperativa JEP", "Cooperativa Policía Nacional", "Otro"
];

const PD_T = {
  es: {
    loading: "Cargando…",
    title: "Mi espacio de socio referidor",
    totalRef: "Referidos totales",
    confirmed: "Confirmados",
    thisMonth: "Este mes",
    balance: "Saldo acumulado",
    payoutInfo: n => `Umbral de pago: $${n} · o abono trimestral.`,
    nextPayout: "Próximo pago",
    nextPayoutInfo: n => `Se abona cuando el saldo alcanza $${n} o al final de cada trimestre.`,
    codeAndLink: "Tu código y enlace",
    waText: (code, link) => `Pruebe Agendik 30 días gratis con mi código ${code}: ${link}`,
    flyer: "Descargar mi flyer (PDF A5)",
    story: "Descargar Instagram Story (1080×1920)",
    presentation: "Descargar presentación (PDF)",
    videoTitle: "Video de presentación (14 s)",
    videoLead: "Muéstralo a los médicos o envíalo por WhatsApp.",
    videoDownload: "Descargar video (MP4)",
    bankIncomplete: "Completa tus datos bancarios para recibir tus comisiones.",
    bankTitle: "Datos bancarios para recibir tus comisiones",
    bankLead: "Pagamos tus comisiones en USD a tu cuenta bancaria en Ecuador, vía Wise, sin costo de envío para ti. Completa tus datos para recibir tus pagos.",
    holderName: "Nombre completo del titular",
    holderId: "Cédula del titular",
    bank: "Banco",
    select: "Seleccionar…",
    otherBank: "Nombre del banco",
    accountType: "Tipo de cuenta",
    accTypePh: "Ahorros / Corriente",
    savings: "Ahorros",
    current: "Corriente",
    accountNumber: "Número de cuenta",
    accountNumberCur: n => `· actual: ${n}`,
    accountDigitsPh: "Sólo dígitos",
    holderEmail: "Email del titular",
    holderPhone: "Teléfono del titular",
    saveBtn: "Guardar datos bancarios",
    savingBtn: "Guardando…",
    saved: "Datos bancarios guardados",
    noReferrals: "Aún no tienes referidos.",
    confirmedBadge: "Confirmado ($9)",
    trialBadge: "Prueba",
    history: "Historial de comisiones",
    exportCsv: "Exportar CSV",
    noCredits: "Todavía no hay comisiones acreditadas. Cada factura pagada por uno de tus médicos referidos agrega $9 aquí.",
    thDate: "Fecha", thType: "Tipo", thDoctor: "Médico referido", thRef: "Referencia", thAmount: "Monto",
    payoutBadge: "Pago recibido",
    commissionBadge: "Comisión",
    lastPaid: "Último pago recibido:",
    errorGeneric: "Error",
  },
  fr: {
    loading: "Chargement…",
    title: "Mon espace socio referidor",
    totalRef: "Parrainages totaux",
    confirmed: "Confirmés",
    thisMonth: "Ce mois-ci",
    balance: "Solde accumulé",
    payoutInfo: n => `Seuil de paiement : $${n} · ou versement trimestriel.`,
    nextPayout: "Prochain versement",
    nextPayoutInfo: n => `Versé lorsque le solde atteint $${n} ou à la fin de chaque trimestre.`,
    codeAndLink: "Votre code et votre lien",
    waText: (code, link) => `Essayez Agendik 30 jours gratuits avec mon code ${code} : ${link}`,
    flyer: "Télécharger mon flyer (PDF A5)",
    story: "Télécharger Instagram Story (1080×1920)",
    presentation: "Télécharger la présentation (PDF)",
    videoTitle: "Vidéo de présentation (14 s)",
    videoLead: "Montrez-la aux médecins ou envoyez-la par WhatsApp.",
    videoDownload: "Télécharger la vidéo (MP4)",
    bankIncomplete: "Complétez vos coordonnées bancaires pour recevoir vos commissions.",
    bankTitle: "Coordonnées bancaires pour recevoir vos commissions",
    bankLead: "Nous payons vos commissions en USD sur votre compte bancaire en Équateur, via Wise, sans frais d'envoi pour vous. Complétez vos coordonnées pour recevoir vos paiements.",
    holderName: "Nom complet du titulaire",
    holderId: "Cédula du titulaire",
    bank: "Banque",
    select: "Sélectionner…",
    otherBank: "Nom de la banque",
    accountType: "Type de compte",
    accTypePh: "Épargne / Courant",
    savings: "Épargne",
    current: "Courant",
    accountNumber: "Numéro de compte",
    accountNumberCur: n => `· actuel : ${n}`,
    accountDigitsPh: "Chiffres uniquement",
    holderEmail: "Email du titulaire",
    holderPhone: "Téléphone du titulaire",
    saveBtn: "Enregistrer les coordonnées bancaires",
    savingBtn: "Enregistrement…",
    saved: "Coordonnées bancaires enregistrées",
    noReferrals: "Vous n'avez pas encore de filleuls.",
    confirmedBadge: "Confirmé ($9)",
    trialBadge: "Essai",
    history: "Historique des commissions",
    exportCsv: "Exporter en CSV",
    noCredits: "Aucune commission n'a encore été créditée. Chaque facture payée par un de vos médecins parrainés ajoute $9 ici.",
    thDate: "Date", thType: "Type", thDoctor: "Médecin parrainé", thRef: "Référence", thAmount: "Montant",
    payoutBadge: "Paiement reçu",
    commissionBadge: "Commission",
    lastPaid: "Dernier paiement reçu :",
    errorGeneric: "Erreur",
  },
  en: {
    loading: "Loading…",
    title: "My Socio referidor dashboard",
    totalRef: "Total referrals",
    confirmed: "Confirmed",
    thisMonth: "This month",
    balance: "Accumulated balance",
    payoutInfo: n => `Payout threshold: $${n} · or quarterly payout.`,
    nextPayout: "Next payout",
    nextPayoutInfo: n => `Paid when balance reaches $${n} or at the end of each quarter.`,
    codeAndLink: "Your code and link",
    waText: (code, link) => `Try Agendik free for 30 days with my code ${code}: ${link}`,
    flyer: "Download my flyer (PDF A5)",
    story: "Download Instagram Story (1080×1920)",
    presentation: "Download presentation (PDF)",
    videoTitle: "Pitch video (14s)",
    videoLead: "Show it to doctors or send it via WhatsApp.",
    videoDownload: "Download video (MP4)",
    bankIncomplete: "Complete your banking details to receive your commissions.",
    bankTitle: "Banking details to receive your commissions",
    bankLead: "We pay your commissions in USD to your bank account in Ecuador, via Wise, at no sending cost to you. Fill in your details to receive your payouts.",
    holderName: "Account holder's full name",
    holderId: "Account holder's cédula",
    bank: "Bank",
    select: "Select…",
    otherBank: "Bank name",
    accountType: "Account type",
    accTypePh: "Savings / Checking",
    savings: "Savings",
    current: "Checking",
    accountNumber: "Account number",
    accountNumberCur: n => `· current: ${n}`,
    accountDigitsPh: "Digits only",
    holderEmail: "Account holder's email",
    holderPhone: "Account holder's phone",
    saveBtn: "Save banking details",
    savingBtn: "Saving…",
    saved: "Banking details saved",
    noReferrals: "You have no referrals yet.",
    confirmedBadge: "Confirmed ($9)",
    trialBadge: "Trial",
    history: "Commission history",
    exportCsv: "Export CSV",
    noCredits: "No commissions credited yet. Each invoice paid by one of your referred doctors adds $9 here.",
    thDate: "Date", thType: "Type", thDoctor: "Referred doctor", thRef: "Reference", thAmount: "Amount",
    payoutBadge: "Payout received",
    commissionBadge: "Commission",
    lastPaid: "Last payout received:",
    errorGeneric: "Error",
  },
};

export default function PartnerDashboard() {
  const { lang } = useApp();
  const t = PD_T[lang] || PD_T.es;
  const [me, setMe] = useState(null);
  const [copied, setCopied] = useState(false);
  const [credits, setCredits] = useState(null);
  const [pay, setPay] = useState(null);
  const [payDraft, setPayDraft] = useState(null);
  const [savingPay, setSavingPay] = useState(false);

  useEffect(() => {
    api.get("/partner/me").then(r => setMe(r.data));
    api.get("/partner/credits").then(r => setCredits(r.data.credits));
    api.get("/partner/payment-info").then(r => { setPay(r.data); setPayDraft(r.data); });
  }, []);
  if (!me) return <div className="max-w-3xl mx-auto p-8 text-slate-500">{t.loading}</div>;

  const link = `https://www.agendik.io/?ref=${me.code}`;
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const wa = `https://wa.me/?text=${encodeURIComponent(t.waText(me.code, link))}`;
  const downloadFlyer = async () => {
    try {
      const r = await api.get("/partner/flyer", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `agendik-flyer-${me.code}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };
  const downloadStory = async () => {
    try {
      const r = await api.get("/partner/flyer-story", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `agendik-story-${me.code}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };
  const downloadPresentation = async () => {
    try {
      const r = await api.get("/partner/presentation", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = "agendik-presentacion-medicos.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const threshold = me.payout_threshold_usd ?? 27;

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">{t.title}</h1>
      <p className="text-slate-500 text-sm mb-6">{me.name} · {me.email}</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-5">
          <Users size={20} className="text-blue-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.totalRef}</div>
          <div className="text-3xl font-bold text-slate-900 mt-1">{me.referred_count}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <Users size={20} className="text-emerald-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.confirmed}</div>
          <div className="text-3xl font-bold text-slate-900 mt-1">{me.confirmed_count}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <DollarSign size={20} className="text-amber-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.thisMonth}</div>
          <div className="text-3xl font-bold text-slate-900 mt-1">${me.earnings_this_month}</div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card className="border-emerald-100 bg-emerald-50/40"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">{t.balance}</div>
          <div data-testid="partner-balance" className="text-3xl font-bold text-emerald-800 mt-1">
            ${(me.accumulated_balance ?? 0).toFixed(2)}
          </div>
          <p className="text-[11px] text-slate-600 mt-2">{t.payoutInfo(threshold)}</p>
        </CardContent></Card>
        <Card className="border-blue-100 bg-blue-50/40"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">{t.nextPayout}</div>
          <div data-testid="partner-next-payout" className="text-2xl font-bold text-blue-800 mt-1">
            {me.next_payout_date ? formatDateShort(me.next_payout_date) : "—"}
          </div>
          <p className="text-[11px] text-slate-600 mt-2">{t.nextPayoutInfo(threshold)}</p>
        </CardContent></Card>
      </div>

      <Card className="mb-6"><CardContent className="p-5">
        <h2 className="font-semibold text-slate-900 mb-3">{t.codeAndLink}</h2>
        <div className="flex items-center gap-2">
          <div className="text-2xl font-bold font-mono text-blue-700">{me.code}</div>
        </div>
        <div className="flex gap-2 mt-3">
          <Input readOnly value={link} data-testid="partner-link" className="font-mono text-xs"/>
          <Button data-testid="partner-copy-btn" size="icon" variant="outline" onClick={copy}>
            {copied ? <Check size={14} className="text-emerald-600"/> : <Copy size={14}/>}
          </Button>
          <Button data-testid="partner-wa-btn" size="sm" onClick={() => window.open(wa, "_blank")}
                  className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap">WhatsApp</Button>
        </div>
        <Button data-testid="partner-flyer-btn" size="sm" onClick={downloadFlyer}
                className="mt-3 mr-2 bg-blue-600 hover:bg-blue-700">
          {t.flyer}
        </Button>
        <Button data-testid="partner-story-btn" size="sm" onClick={downloadStory}
                className="mt-3 mr-2 bg-pink-600 hover:bg-pink-700">
          {t.story}
        </Button>
        <Button data-testid="partner-presentation-btn" size="sm" onClick={downloadPresentation}
                className="mt-3 bg-slate-800 hover:bg-slate-900">
          {t.presentation}
        </Button>
      </CardContent></Card>

      <Card className="mb-6"><CardContent className="p-5">
        <h2 className="font-semibold text-slate-900 mb-1">{t.videoTitle}</h2>
        <p className="text-xs text-slate-500 mb-3">{t.videoLead}</p>
        <video data-testid="partner-video"
               src="/videos/agendik-pub-video-9x16-15s.mp4"
               controls playsInline preload="metadata"
               className="w-full sm:max-w-[320px] h-auto rounded-2xl shadow-sm"/>
        <div className="mt-3">
          <a data-testid="partner-video-download"
             href="/videos/agendik-pub-video-9x16-15s.mp4"
             download="agendik-pub-video-9x16-15s.mp4"
             className="inline-flex items-center justify-center h-9 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
            {t.videoDownload}
          </a>
        </div>
      </CardContent></Card>

      {(() => {
        const bankIncomplete = !pay || !pay.bank_titular || !pay.id_or_ruc
                                || (!pay.bank_name && !pay.bank_name_other)
                                || !pay.account_type || !pay.account_number_masked
                                || !pay.holder_email || !pay.holder_phone;
        return bankIncomplete ? (
          <div data-testid="bank-incomplete-banner" className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            {t.bankIncomplete}
          </div>
        ) : null;
      })()}
      <Card className="mb-6" data-testid="partner-payment-card"><CardContent className="p-5">
        <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2"><Banknote size={16}/> {t.bankTitle}</h2>
        <p className="text-xs text-slate-500 mb-4">{t.bankLead}</p>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">{t.holderName}</Label>
              <Input data-testid="pay-bank-titular" value={payDraft?.bank_titular || ""}
                     onChange={e => setPayDraft({...(payDraft||{}), bank_titular: e.target.value})}/></div>
            <div><Label className="text-xs">{t.holderId}</Label>
              <Input data-testid="pay-bank-idrc" value={payDraft?.id_or_ruc || ""}
                     onChange={e => setPayDraft({...(payDraft||{}), id_or_ruc: e.target.value})}/></div>
            <div><Label className="text-xs">{t.bank}</Label>
              <Select value={payDraft?.bank_name || ""} onValueChange={v => setPayDraft({...(payDraft||{}), bank_name: v})}>
                <SelectTrigger data-testid="pay-bank-name" className="h-10"><SelectValue placeholder={t.select}/></SelectTrigger>
                <SelectContent>{EC_BANKS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
              {payDraft?.bank_name === "Otro" && (
                <Input data-testid="pay-bank-other" className="mt-2" placeholder={t.otherBank}
                       value={payDraft?.bank_name_other || ""}
                       onChange={e => setPayDraft({...(payDraft||{}), bank_name_other: e.target.value})}/>
              )}
            </div>
            <div><Label className="text-xs">{t.accountType}</Label>
              <Select value={payDraft?.account_type || ""} onValueChange={v => setPayDraft({...(payDraft||{}), account_type: v})}>
                <SelectTrigger data-testid="pay-account-type" className="h-10"><SelectValue placeholder={t.accTypePh}/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ahorros">{t.savings}</SelectItem>
                  <SelectItem value="corriente">{t.current}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">{t.accountNumber} {pay?.account_number_masked && <span className="text-slate-400">{t.accountNumberCur(pay.account_number_masked)}</span>}</Label>
              <Input data-testid="pay-account-number" inputMode="numeric" placeholder={t.accountDigitsPh}
                     value={payDraft?.account_number || ""}
                     onChange={e => setPayDraft({...(payDraft||{}), account_number: e.target.value.replace(/\D/g,"")})}/></div>
            <div><Label className="text-xs">{t.holderEmail}</Label>
              <Input data-testid="pay-holder-email" type="email" value={payDraft?.holder_email || ""}
                     onChange={e => setPayDraft({...(payDraft||{}), holder_email: e.target.value})}/></div>
            <div><Label className="text-xs">{t.holderPhone}</Label>
              <Input data-testid="pay-holder-phone" value={payDraft?.holder_phone || ""}
                     onChange={e => setPayDraft({...(payDraft||{}), holder_phone: e.target.value})}/></div>
          </div>
        </div>

        <Button data-testid="pay-save-btn" disabled={savingPay} onClick={async () => {
          setSavingPay(true);
          try {
            await api.put("/partner/payment-info", payDraft || {});
            const r = await api.get("/partner/payment-info");
            setPay(r.data); setPayDraft({...r.data, account_number: ""});
            toast.success(t.saved);
          } catch (e) { toast.error(e.response?.data?.detail || t.errorGeneric); }
          finally { setSavingPay(false); }
        }} className="mt-4 bg-blue-600 hover:bg-blue-700">
          {savingPay ? t.savingBtn : t.saveBtn}
        </Button>
      </CardContent></Card>

      <Card><CardContent className="p-5">
        {me.referred.length === 0 ? <p className="text-sm text-slate-500">{t.noReferrals}</p> :
          <div className="space-y-2">
            {me.referred.map((d, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-slate-100">
                <div className="text-sm text-slate-700">{drName(d.full_name)}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${d.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {d.subscription_status === "active" ? t.confirmedBadge : t.trialBadge}
                </span>
              </div>
            ))}
          </div>}
      </CardContent></Card>

      <Card><CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><ReceiptText size={16}/> {t.history}</h2>
          {credits && credits.length > 0 && (
            <Button data-testid="partner-credits-csv" size="sm" variant="outline" onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/partner/credits.csv`;
              const token = localStorage.getItem("agendik_token");
              fetch(url, { headers: { Authorization: `Bearer ${token}` }}).then(r => r.blob()).then(b => {
                const a = document.createElement("a"); a.href = URL.createObjectURL(b);
                a.download = `agendik-comisiones-${me.code}.csv`; a.click(); URL.revokeObjectURL(a.href);
              });
            }} className="h-8 text-xs">{t.exportCsv}</Button>
          )}
        </div>
        {credits === null ? <p className="text-sm text-slate-500">{t.loading}</p> :
         credits.length === 0 ? <p className="text-sm text-slate-500">{t.noCredits}</p> :
         <div className="overflow-x-auto"><table data-testid="partner-credits-table" className="w-full text-sm">
           <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
             <tr>
               <th className="p-2 text-left">{t.thDate}</th>
               <th className="p-2 text-left">{t.thType}</th>
               <th className="p-2 text-left">{t.thDoctor}</th>
               <th className="p-2 text-left">{t.thRef}</th>
               <th className="p-2 text-right">{t.thAmount}</th>
             </tr>
           </thead>
           <tbody>{credits.map((c, i) => (
             <tr key={i} data-testid={`credit-row-${i}`} className="border-t border-slate-100">
               <td className="p-2 text-xs text-slate-700">{formatDateShort(c.invoice_paid_at || c.created_at)}</td>
               <td className="p-2">
                 {c.kind === "payout"
                   ? <Badge className="bg-slate-200 text-slate-700 border-slate-300 hover:bg-slate-200 text-[10px]">{t.payoutBadge}</Badge>
                   : <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 text-[10px]">{t.commissionBadge}</Badge>}
               </td>
               <td className="p-2 text-xs">{c.doctor_name ? drName(c.doctor_name) : "—"}</td>
               <td className="p-2 text-[10px] font-mono text-slate-400 truncate max-w-[180px]" title={c.note || c.invoice_id}>{c.note || c.invoice_id}</td>
               <td className={`p-2 text-right font-semibold ${c.amount_usd < 0 ? "text-slate-600" : "text-emerald-700"}`}>
                 {c.amount_usd < 0 ? "" : "+"}${c.amount_usd}
               </td>
             </tr>
           ))}</tbody>
         </table></div>}
      </CardContent></Card>

      <div className="text-xs text-slate-500 mt-4">
        {t.lastPaid} {me.last_paid_at ? formatDateShort(me.last_paid_at) : "—"}
      </div>
    </div>
  );
}
